if (!customElements.get('td-before-after-tabs')) {
  class TDBeforeAfterTabs extends HTMLElement {
    static SELECTORS = {
      tabTrigger: '[data-td-tab-trigger]',
      card: '[data-td-before-after-card]',
      allPanel: '[data-td-all-panel]',
      reviewBody: '[data-td-review-body]',
      readMore: '[data-td-review-read-more]',
    };

    constructor() {
      super();
      this.handleClick = this.handleClick.bind(this);
      this.handleBlockSelect = this.handleBlockSelect.bind(this);
      this.handleHashChange = this.handleHashChange.bind(this);
      this.updateReadMoreControls = this.updateReadMoreControls.bind(this);
      this.readMoreObserver = null;
      this.measureFrame = 0;
    }

    connectedCallback() {
      this.addEventListener('click', this.handleClick);
      document.addEventListener('shopify:block:select', this.handleBlockSelect);
      window.addEventListener('hashchange', this.handleHashChange);
      this.observeReviewBodies();
      if (!this.openFromHash()) this.activateTab('all');
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleClick);
      document.removeEventListener('shopify:block:select', this.handleBlockSelect);
      window.removeEventListener('hashchange', this.handleHashChange);
      if (this.measureFrame) window.cancelAnimationFrame(this.measureFrame);
      this.readMoreObserver?.disconnect();
      this.readMoreObserver = null;
    }

    handleClick(event) {
      const readMore = event.target.closest(TDBeforeAfterTabs.SELECTORS.readMore);
      if (readMore && this.contains(readMore)) {
        this.expandReview(readMore);
        return;
      }

      const trigger = event.target.closest(TDBeforeAfterTabs.SELECTORS.tabTrigger);
      if (!trigger || !this.contains(trigger)) return;

      this.activateTab(trigger.dataset.tdTabFilter);
      this.syncHash(trigger);
    }

    observeReviewBodies() {
      const bodies = this.querySelectorAll(TDBeforeAfterTabs.SELECTORS.reviewBody);

      if (typeof ResizeObserver === 'function') {
        this.readMoreObserver = new ResizeObserver(this.updateReadMoreControls);
        bodies.forEach((body) => this.readMoreObserver.observe(body));
      }

      this.measureFrame = window.requestAnimationFrame(() => {
        this.measureFrame = 0;
        this.updateReadMoreControls();
      });
    }

    updateReadMoreControls() {
      this.querySelectorAll(TDBeforeAfterTabs.SELECTORS.readMore).forEach((button) => {
        const card = button.closest(TDBeforeAfterTabs.SELECTORS.card);
        const body = card?.querySelector(TDBeforeAfterTabs.SELECTORS.reviewBody);

        if (!card || !body || card.dataset.tdReviewExpanded === 'true') return;

        button.hidden = body.scrollHeight <= body.clientHeight + 1;
      });
    }

    expandReview(button) {
      const card = button.closest(TDBeforeAfterTabs.SELECTORS.card);
      const body = card?.querySelector(TDBeforeAfterTabs.SELECTORS.reviewBody);
      if (!card || !body) return;

      card.dataset.tdReviewExpanded = 'true';
      button.setAttribute('aria-expanded', 'true');
      button.hidden = true;
    }

    handleBlockSelect(event) {
      const selectedTrigger = event.target.closest(TDBeforeAfterTabs.SELECTORS.tabTrigger);
      if (selectedTrigger && this.contains(selectedTrigger)) {
        this.activateTab(selectedTrigger.dataset.tdTabFilter);
        return;
      }

      const selectedCard = event.target.closest(TDBeforeAfterTabs.SELECTORS.card);
      if (!selectedCard || !this.contains(selectedCard)) return;

      const productIds = this.productIdsForCard(selectedCard);
      const trigger = productIds
        .map((productId) =>
          this.querySelector(
            `${TDBeforeAfterTabs.SELECTORS.tabTrigger}[data-td-tab-filter="${CSS.escape(productId)}"]`
          )
        )
        .find(Boolean);

      this.activateTab(trigger?.dataset.tdTabFilter || 'all');
    }

    handleHashChange() {
      this.openFromHash();
    }

    static slug(value) {
      return String(value || '')
        .toLowerCase()
        .replace(/^tab[-_]/, '')
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    static anchorFor(trigger) {
      return (
        TDBeforeAfterTabs.slug(trigger.dataset.tdTabAnchor) ||
        TDBeforeAfterTabs.slug(trigger.dataset.tdTabFilter) ||
        TDBeforeAfterTabs.slug(trigger.dataset.tdTabBlock)
      );
    }

    tabFromHash(hash) {
      const wanted = TDBeforeAfterTabs.slug(decodeURIComponent(String(hash || '').replace(/^#/, '')));
      if (!wanted) return null;

      return (
        [...this.querySelectorAll(TDBeforeAfterTabs.SELECTORS.tabTrigger)].find(
          (trigger) => TDBeforeAfterTabs.anchorFor(trigger) === wanted
        ) || null
      );
    }

    openFromHash() {
      const trigger = this.tabFromHash(window.location.hash);
      if (!trigger) return false;

      this.activateTab(trigger.dataset.tdTabFilter);
      return true;
    }

    syncHash(trigger) {
      const slug = TDBeforeAfterTabs.anchorFor(trigger);
      if (!slug || typeof window.history?.replaceState !== 'function') return;

      const { pathname, search } = window.location;
      window.history.replaceState(window.history.state, '', `${pathname}${search}#${slug}`);
    }

    productIdsForCard(card) {
      return String(card.dataset.tdProductIds || '')
        .split(',')
        .map((productId) => productId.trim())
        .filter(Boolean);
    }

    activateTab(filter) {
      const activeFilter = String(filter || 'all');
      const isAllActive = activeFilter === 'all';

      this.querySelectorAll(TDBeforeAfterTabs.SELECTORS.tabTrigger).forEach((trigger) => {
        const isActive = trigger.dataset.tdTabFilter === activeFilter;
        trigger.classList.toggle('td-before-after-tabs__tab--active', isActive);
        trigger.setAttribute('aria-selected', String(isActive));
      });

      this.querySelectorAll(TDBeforeAfterTabs.SELECTORS.card).forEach((card) => {
        card.hidden = !isAllActive && !this.productIdsForCard(card).includes(activeFilter);
      });

      const allPanel = this.querySelector(TDBeforeAfterTabs.SELECTORS.allPanel);
      if (allPanel) allPanel.dataset.tdView = isAllActive ? 'all' : 'product';
    }
  }

  customElements.define('td-before-after-tabs', TDBeforeAfterTabs);
}
